import { useMutation, useQuery } from '@tanstack/react-query';
import axios from 'axios';
import QueryKeys from '../../../utils/QueryKeys';
import { plato_api_reference_materials_path, plato_api_slide_path } from '../../../modules/routes';
import IReferenceMaterial from '../../interfaces/ReferenceMaterial';
import showToast from '../../common/Toast';

export type ReferenceMaterialResponse = Pick<IReferenceMaterial, 'id' | 'title' | 'tag_values' | 'is_assigned' | 'assignable_type' | 'max_score'>;

export function useReferenceMaterials() {
  return useQuery<ReferenceMaterialResponse[]>({
    placeholderData: [],
    queryFn: async () => {
      const path = plato_api_reference_materials_path({
        includes: 'translations',
        only: ['id', 'title', 'published'],
        order: 'asc',
        page: 1,
        per_page: 1000,
        published: true,
      });
      const response = await axios.get<{ data: ReferenceMaterialResponse[] }>(path);

      return response.data.data.filter(material => !!material.title).sort((a, b) => a.title.localeCompare(b.title));
    },
    queryKey: [QueryKeys.ReferenceMaterials, ['id', 'title', 'published']],
  });
}

export function useSlideReferenceMaterials(slideId: number, lessonId?: number) {
  return useQuery<ReferenceMaterialResponse[]>({
    placeholderData: [],
    queryFn: () => (
      axios
        .get<{ data: ReferenceMaterialResponse[] }>(plato_api_reference_materials_path({
          lesson_id: lessonId,
          for_model: { model_id: slideId, model_type: 'Slide' },
          includes: 'translations',
          only: ['id', 'title', 'tag_values', 'is_assigned', 'assignable_type', 'max_score'],
          page: 1,
          per_page: 1000,
        }))
        .then(res => res.data.data)
        .catch(() => ([]))
    ),
    queryKey: [QueryKeys.Slide, QueryKeys.ReferenceMaterial, slideId, ['id', 'title']],
  });
}

export function useSlideReferenceMaterialsMutation(slideId: number) {
  return useMutation({
    mutationFn: ({ ids }: { ids: number[] }) => (
      axios
        .patch(plato_api_slide_path(slideId), { reference_material_ids: ids })
        .then(() => {
          showToast('Successfully saved tags', { position: 'top-right' });
        })
    ),
    mutationKey: [QueryKeys.ModelReferenceMaterials, slideId],
  });
}
